import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Plus, Trash2, ChevronDown, ChevronRight,
  RefreshCw, Upload, Download, Copy
} from 'lucide-react';
import { useTemplateStore } from '../store/templateStore';

export function VariableDataEditor() {
  const { previewData, setPreviewData } = useTemplateStore();
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set(['', 'grantor', 'grantee', 'property']));
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState(JSON.stringify(previewData, null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);

  const toggleExpand = (path: string) => {
    const newExpanded = new Set(expandedPaths);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedPaths(newExpanded);
  };

  const updateValue = (path: string, value: any) => {
    const parts = path.split('.');
    const newData = { ...previewData };
    let current: any = newData;
    
    for (let i = 0; i < parts.length - 1; i++) {
      current[parts[i]] = { ...current[parts[i]] };
      current = current[parts[i]];
    }
    
    // Parse value type
    if (value === 'true') value = true;
    else if (value === 'false') value = false;
    else if (value === 'null') value = null;
    else if (!isNaN(Number(value)) && value !== '') value = Number(value);
    
    current[parts[parts.length - 1]] = value;
    setPreviewData(newData);
    setJsonText(JSON.stringify(newData, null, 2));
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      setPreviewData(parsed);
      setJsonError(null);
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  };

  const resetToDefaults = () => {
    const defaults = {
      grantor: {
        type: 'individual',
        firstName: 'John',
        middleName: 'William',
        lastName: 'Smith',
        maritalStatus: 'married',
        spouse: {
          firstName: 'Jane',
          middleName: 'Marie',
          lastName: 'Smith'
        }
      },
      grantee: {
        type: 'individual',
        firstName: 'Robert',
        middleName: 'James',
        lastName: 'Johnson',
        maritalStatus: 'single'
      },
      property: {
        address: {
          street1: '123 Main Street',
          city: 'Sturgeon Bay',
          state: 'WI',
          zipCode: '54235',
          county: 'Door'
        },
        legalDescription: 'Lot 5, Block 2, of SUNSET ADDITION to the City of Sturgeon Bay, Door County, Wisconsin, according to the recorded plat thereof.',
        parcelId: '012-0123-4567-000'
      },
      consideration: 350000,
      executionDate: new Date().toISOString(),
      preparedBy: {
        name: 'Seth K. Goettelman',
        barNumber: '1089234',
        firm: 'Goettelman Law'
      }
    };
    setPreviewData(defaults);
    setJsonText(JSON.stringify(defaults, null, 2));
  };

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-primary-700/50 bg-primary-900/30">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setJsonMode(false)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              !jsonMode ? 'bg-accent-500/20 text-accent-400' : 'text-primary-400 hover:text-white'
            }`}
          >
            Visual
          </button>
          <button
            onClick={() => setJsonMode(true)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              jsonMode ? 'bg-accent-500/20 text-accent-400' : 'text-primary-400 hover:text-white'
            }`}
          >
            JSON
          </button>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={resetToDefaults}
            className="btn-ghost flex items-center gap-2"
            title="Reset to defaults"
          >
            <RefreshCw className="w-4 h-4" />
            Reset
          </button>
          <button className="btn-ghost" title="Import JSON">
            <Upload className="w-4 h-4" />
          </button>
          <button className="btn-ghost" title="Export JSON">
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {jsonMode ? (
          <div className="h-full flex flex-col">
            {jsonError && (
              <div className="mb-4 p-3 bg-error-500/10 border border-error-500/30 rounded-lg text-sm text-error-400">
                {jsonError}
              </div>
            )}
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              className="flex-1 w-full p-4 bg-primary-900/50 border border-primary-700/50 rounded-lg font-mono text-sm text-primary-100 resize-none focus:outline-none focus:border-accent-500/50"
              placeholder="Enter JSON data..."
            />
            <button
              onClick={applyJson}
              className="mt-4 btn-primary self-end"
            >
              Apply Changes
            </button>
          </div>
        ) : (
          <div className="space-y-1">
            <ObjectEditor
              data={previewData}
              path=""
              expandedPaths={expandedPaths}
              onToggleExpand={toggleExpand}
              onUpdateValue={updateValue}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface ObjectEditorProps {
  data: any;
  path: string;
  expandedPaths: Set<string>;
  onToggleExpand: (path: string) => void;
  onUpdateValue: (path: string, value: any) => void;
  depth?: number;
}

function ObjectEditor({
  data,
  path,
  expandedPaths,
  onToggleExpand,
  onUpdateValue,
  depth = 0,
}: ObjectEditorProps) {
  if (data === null || data === undefined) {
    return (
      <div className="text-sm text-primary-400 italic">null</div>
    );
  }

  if (typeof data !== 'object') {
    return (
      <ValueEditor
        value={data}
        path={path}
        onUpdate={onUpdateValue}
      />
    );
  }

  if (Array.isArray(data)) {
    const isExpanded = expandedPaths.has(path);
    
    return (
      <div>
        <button
          onClick={() => onToggleExpand(path)}
          className="flex items-center gap-2 text-sm hover:bg-white/5 rounded px-2 py-1 -ml-2"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-primary-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-primary-400" />
          )}
          <span className="text-primary-300">Array</span>
          <span className="text-primary-500">[{data.length}]</span>
        </button>
        
        {isExpanded && (
          <div className="ml-6 mt-1 space-y-1 border-l border-primary-700/50 pl-4">
            {data.map((item, index) => (
              <div key={index} className="flex items-start gap-2">
                <span className="text-xs text-primary-500 mt-1">{index}</span>
                <div className="flex-1">
                  <ObjectEditor
                    data={item}
                    path={path ? `${path}.${index}` : String(index)}
                    expandedPaths={expandedPaths}
                    onToggleExpand={onToggleExpand}
                    onUpdateValue={onUpdateValue}
                    depth={depth + 1}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Object
  const entries = Object.entries(data);
  const isExpanded = expandedPaths.has(path);
  
  if (depth === 0) {
    // Root level - show all keys
    return (
      <div className="space-y-1">
        {entries.map(([key, value]) => (
          <PropertyEditor
            key={key}
            propertyKey={key}
            value={value}
            path={key}
            expandedPaths={expandedPaths}
            onToggleExpand={onToggleExpand}
            onUpdateValue={onUpdateValue}
            depth={depth}
          />
        ))}
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => onToggleExpand(path)}
        className="flex items-center gap-2 text-sm hover:bg-white/5 rounded px-2 py-1 -ml-2"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-primary-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-primary-400" />
        )}
        <span className="text-primary-300">Object</span>
        <span className="text-primary-500">{`{${entries.length}}`}</span>
      </button>
      
      {isExpanded && (
        <div className="ml-6 mt-1 space-y-1 border-l border-primary-700/50 pl-4">
          {entries.map(([key, value]) => (
            <PropertyEditor
              key={key}
              propertyKey={key}
              value={value}
              path={path ? `${path}.${key}` : key}
              expandedPaths={expandedPaths}
              onToggleExpand={onToggleExpand}
              onUpdateValue={onUpdateValue}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface PropertyEditorProps {
  propertyKey: string;
  value: any;
  path: string;
  expandedPaths: Set<string>;
  onToggleExpand: (path: string) => void;
  onUpdateValue: (path: string, value: any) => void;
  depth: number;
}

function PropertyEditor({
  propertyKey,
  value,
  path,
  expandedPaths,
  onToggleExpand,
  onUpdateValue,
  depth,
}: PropertyEditorProps) {
  const isObject = value !== null && typeof value === 'object';
  const isExpanded = expandedPaths.has(path);

  if (isObject) {
    return (
      <div>
        <button
          onClick={() => onToggleExpand(path)}
          className="flex items-center gap-2 text-sm hover:bg-white/5 rounded px-2 py-1 -ml-2 w-full text-left"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-primary-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-primary-400" />
          )}
          <span className="text-accent-400 font-medium">{propertyKey}</span>
          <span className="text-primary-500">
            {Array.isArray(value) ? `[${value.length}]` : `{${Object.keys(value).length}}`}
          </span>
        </button>
        
        {isExpanded && (
          <div className="ml-6 mt-1 space-y-1 border-l border-primary-700/50 pl-4">
            {Object.entries(value).map(([k, v]) => (
              <PropertyEditor
                key={k}
                propertyKey={k}
                value={v}
                path={`${path}.${k}`}
                expandedPaths={expandedPaths}
                onToggleExpand={onToggleExpand}
                onUpdateValue={onUpdateValue}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 py-1">
      <span className="text-sm text-accent-400 font-medium min-w-[100px]">
        {propertyKey}
      </span>
      <ValueEditor
        value={value}
        path={path}
        onUpdate={onUpdateValue}
      />
    </div>
  );
}

interface ValueEditorProps {
  value: any;
  path: string;
  onUpdate: (path: string, value: any) => void;
}

function ValueEditor({ value, path, onUpdate }: ValueEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value ?? ''));

  const getValueType = (v: any): string => {
    if (v === null) return 'null';
    if (typeof v === 'boolean') return 'boolean';
    if (typeof v === 'number') return 'number';
    if (typeof v === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(v)) return 'date';
      return 'string';
    }
    return typeof v;
  };

  const type = getValueType(value);

  const handleSave = () => {
    onUpdate(path, editValue);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 flex-1">
        {type === 'boolean' ? (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            className="px-2 py-1 bg-primary-800 border border-accent-500/50 rounded text-sm text-white focus:outline-none"
            autoFocus
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : (
          <input
            type={type === 'number' ? 'number' : 'text'}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') setIsEditing(false);
            }}
            className="flex-1 px-2 py-1 bg-primary-800 border border-accent-500/50 rounded text-sm text-white focus:outline-none"
            autoFocus
          />
        )}
      </div>
    );
  }

  const getColorClass = () => {
    switch (type) {
      case 'string': return 'text-green-400';
      case 'number': return 'text-blue-400';
      case 'boolean': return 'text-purple-400';
      case 'date': return 'text-cyan-400';
      case 'null': return 'text-gray-500 italic';
      default: return 'text-primary-300';
    }
  };

  const formatValue = () => {
    if (value === null) return 'null';
    if (type === 'string') return `"${value}"`;
    if (type === 'date') return new Date(value).toLocaleDateString();
    return String(value);
  };

  return (
    <button
      onClick={() => {
        setEditValue(String(value ?? ''));
        setIsEditing(true);
      }}
      className={`text-sm font-mono ${getColorClass()} hover:bg-white/5 px-2 py-1 rounded transition-colors text-left`}
    >
      {formatValue()}
    </button>
  );
}
